import { useEffect, useState } from "react";
import axios from "axios";
import { Bookmark, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AppLayout, { PageHeader, ComingSoonNote } from "@/components/app/AppLayout";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = [
  { id: "", label: "Todo" },
  { id: "post", label: "Publicaciones" },
  { id: "listing", label: "Anuncios" },
  { id: "place", label: "Lugares" },
  { id: "track", label: "Música" },
  { id: "user", label: "Personas" },
];

export default function Saved() {
  const auth = useAuth();
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");

  const load = () => {
    if (!auth?.user || typeof auth.user !== "object") return;
    const p = type ? `?target_type=${type}` : "";
    axios.get(`${API}/bookmarks${p}`, { headers: auth.apiHeaders() }).then((r) => setItems(r.data || []));
  };
  useEffect(load, [type, auth]); // eslint-disable-line

  const remove = async (b) => {
    try {
      await axios.delete(`${API}/bookmarks/${b.id}`, { headers: auth.apiHeaders() });
      setItems((arr) => arr.filter((x) => x.id !== b.id));
      toast.success("Guardado eliminado");
    } catch { toast.error("Error"); }
  };

  if (!auth?.user || typeof auth.user !== "object") {
    return (
      <AppLayout>
        <PageHeader kicker="◊ Guardados" title="Guardados." subtitle="Inicia sesión para guardar publicaciones, anuncios, lugares, música y personas." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader kicker="◊ Guardados" title="Guardados." subtitle="Tu colección personal: posts, productos, lugares, música y perfiles que quieres revisitar." />

      <div className="flex gap-1.5 mb-4 flex-wrap">
        {TYPES.map((t) => (
          <button key={t.id} data-testid={`saved-filter-${t.id || "all"}`} onClick={() => setType(t.id)} className={`text-xs px-3 py-1.5 rounded-full font-mono ${type === t.id ? "bg-ink text-cream" : "bg-cream-card hairline border text-ink-mid"}`}>{t.label}</button>
        ))}
      </div>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="saved-grid">
        {items.length === 0 && (
          <div className="col-span-full text-center py-12" data-testid="saved-empty">
            <Bookmark className="w-10 h-10 text-ink-mid mx-auto mb-3" />
            <p className="text-sm text-ink-mid">Aún no has guardado nada. Toca el ícono de marcador en cualquier post o anuncio.</p>
          </div>
        )}
        {items.map((b) => (
          <article key={b.id} data-testid={`saved-${b.id}`} className="border hairline bg-cream-card rounded-2xl overflow-hidden">
            <div className="aspect-video bg-cream-deep">
              {b.image && <img src={b.image} alt={b.title} className="w-full h-full object-cover" />}
            </div>
            <div className="p-3 flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <p className="font-mono text-[9px] uppercase tracking-wider text-coral">{b.target_type}</p>
                <p className="text-sm font-medium truncate">{b.title || b.target_id}</p>
              </div>
              <button data-testid={`saved-remove-${b.id}`} onClick={() => remove(b)} className="p-1.5 rounded-full hover:bg-red-50 text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </article>
        ))}
      </section>

      <ComingSoonNote feature="botón de marcador en cada post/anuncio/lugar, colecciones, exportar a PDF" eta="próxima fase" />
    </AppLayout>
  );
}
